import React from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { InfoLine } from "../../components/InfoLine";

const CREATOR_EVENT_CONTRACT_EXAMPLES = [
    "ExtraLiftMovementUnauthorized",
    "IPCObjectPositioningCalibrationError",
    "ChargingContactorStateChanged",
    "ChargingContactorOpened",
] as const;

function CreatorEventContractTooltip() {
    return (
        <Stack spacing={0.75} sx={{ maxWidth: 300, py: 0.25 }}>
            <Typography variant="body2" fontWeight={700}>
                Unique event name
            </Typography>
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                Required for VDA 5050 conformity, among others.
            </Typography>
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                English only · <strong>PascalCase</strong> · no spaces
            </Typography>
            <Box>
                <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
                    Examples
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.15 } }}>
                    {CREATOR_EVENT_CONTRACT_EXAMPLES.map((name) => (
                        <Typography
                            key={name}
                            component="li"
                            variant="caption"
                            sx={{ fontFamily: "Consolas, monospace", fontSize: "0.68rem", lineHeight: 1.35 }}
                        >
                            {name}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Stack>
    );
}

function CreatorResolvingContractTooltip() {
    return (
        <Stack spacing={0.75} sx={{ maxWidth: 300, py: 0.25 }}>
            <Typography variant="body2" fontWeight={700}>
                Unique event name
            </Typography>
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                Contract name for this resolving event (VDA 5050, among others). May differ from the rising
                event — use the memory aid to compare.
            </Typography>
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                English only · <strong>PascalCase</strong> · no spaces
            </Typography>
        </Stack>
    );
}

function EditorEventContractTooltip({ isAdmin }: { isAdmin: boolean }) {
    return (
        <Stack spacing={0.75} sx={{ maxWidth: 280, py: 0.25 }}>
            <Typography variant="body2" fontWeight={700}>
                Unique name of the event
            </Typography>
            {!isAdmin && (
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                    Coordinate with an administrator before renaming events that other systems depend on.
                </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                Editable only for version 0 while the event is not approved.
            </Typography>
            {isAdmin && (
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.45 }}>
                    For approved or versioned events, use <strong>Rename</strong> — optional separate names
                    for rising and resolving.
                </Typography>
            )}
        </Stack>
    );
}

export interface EventBaseInfoProps {
    formData: any;
    errors: any;
    isReader: boolean;
    isAdmin: boolean;
    isCreator: boolean;
    theme: any;
    configuratorMode: boolean;
    eventApproved: boolean;
    creatingNewResolvingEvent: boolean;
    handleRisingEventChange: (e: any) => void;
    handleAttributeChange: (e: any) => void;
    handleSelectChange: (e: any) => void;
    handleAttributeSelectChange: (e: any) => void;
    handleCheckboxChange: (e: any) => void;
    onResolveDevComment?: () => void;
    resolveDevCommentBusy?: boolean;
    /** Creator resolving step: contract name assigned on the rising event (memory aid). */
    creatorRisingContractName?: string | null;
    onOpenRename?: () => void;
    renameDisabled?: boolean;
}

export const EventBaseInfo: React.FC<EventBaseInfoProps> = React.memo((props) => {
    // Keep Source support in data model/API, but hide field in UI for now.
    const showSourceField = false;
    // Alarm / message history: kept in API payload; not used in product UI (incl. admins).
    const showAlarmAndMessageHistoryFields = false;
    const {
        formData, errors, isReader, isAdmin, isCreator, theme,
        configuratorMode, eventApproved, creatingNewResolvingEvent,
        handleRisingEventChange, handleAttributeChange,
        handleSelectChange, handleAttributeSelectChange, handleCheckboxChange,
        onResolveDevComment, resolveDevCommentBusy = false,
        creatorRisingContractName = null,
        onOpenRename,
        renameDisabled = false,
    } = props;
    const isCreatorResolvingContract = configuratorMode && creatingNewResolvingEvent;
    const risingContractMemory = String(creatorRisingContractName ?? "").trim();
    const showEventContractNameField = isAdmin || isCreator;
    const showContractNameAtTop = configuratorMode;
    const contractNameTooltip = configuratorMode ? (
        isCreatorResolvingContract ? (
            <CreatorResolvingContractTooltip />
        ) : (
            <CreatorEventContractTooltip />
        )
    ) : (
        <EditorEventContractTooltip isAdmin={isAdmin} />
    );
    const canEditEventCode = isAdmin && !formData.attributes?.manufacturer_event;
    const canEditEventCodeOld =
        (isAdmin || isCreator) &&
        !Boolean(formData.attributes?.manufacturer_event);
    const isVersionZero =
        formData.version == null || Number(formData.version) === 0;
    const canEditEventContractName =
        !isReader &&
        !Boolean(formData.attributes?.manufacturer_event) &&
        isVersionZero &&
        !eventApproved;
    const showRenameButton =
        isAdmin &&
        !configuratorMode &&
        Boolean(formData.global_event_id) &&
        !Boolean(formData.attributes?.manufacturer_event) &&
        !canEditEventContractName &&
        Boolean(onOpenRename);
    // Align icons and inline actions with the vertical center of a labeled TextField input (not helper text).
    const inputCompanionAlignSx = {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        alignSelf: "flex-start",
        height: 56,
    } as const;
    const infoIconGridSx = {
        ...inputCompanionAlignSx,
        color: theme.palette.primary.main,
    } as const;

    const showRisingContractMemory = isCreatorResolvingContract && Boolean(risingContractMemory);
    const contractNameGridSize = showContractNameAtTop
        ? showRisingContractMemory
            ? { field: 7, memory: 4, icon: 1 }
            : { field: 11, icon: 1 }
        : { field: 5, icon: 1 };

    const displayedContractName = String(formData.event_contract_name ?? "");

    const renderDevCommentRow = () => {
        if (!isAdmin || formData.attributes?.manufacturer_event) {
            return null;
        }
        return (
            <>
                <Grid size={{ xs: 11 }}>
                    <TextField
                        label="Development comment"
                        name="dev_comment"
                        color="warning"
                        focused
                        placeholder="Optional admin comment"
                        value={formData.attributes?.dev_comment ?? ""}
                        multiline
                        rows={2}
                        slotProps={{
                            input: {
                                readOnly: isReader || formData.attributes?.manufacturer_event,
                            },
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                        onChange={handleAttributeChange}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 1 }} sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", pt: 1 }}>
                    {String(formData.attributes?.dev_comment ?? "").trim() !== "" && (
                        <Tooltip title="Clear development comment (event version unchanged)" arrow>
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="warning"
                                    disabled={isReader || resolveDevCommentBusy || !formData.global_event_id}
                                    onClick={onResolveDevComment}
                                >
                                    {resolveDevCommentBusy ? "…" : "Resolve"}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Grid>
            </>
        );
    };

    const contractIconSx = showRisingContractMemory
        ? {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            color: theme.palette.primary.main,
        }
        : infoIconGridSx;

    const renderContractNameRow = () => {
        if (!showEventContractNameField) {
            return null;
        }
        const resolvingHelper =
            isCreatorResolvingContract && !displayedContractName.trim()
                ? "Required — may differ from the rising event."
                : undefined;
        const helperMessage = errors["event_contract_name"] || resolvingHelper;

        const contractTextField = (
            <TextField
                label="Event contract name"
                name="event_contract_name"
                value={displayedContractName}
                required={showContractNameAtTop}
                autoFocus={!isReader && showContractNameAtTop && !isAdmin && !isCreatorResolvingContract}
                placeholder="e.g. ChargingContactorOpened"
                slotProps={{
                    input: {
                        readOnly:
                            isReader ||
                            Boolean(formData.attributes?.manufacturer_event) ||
                            !canEditEventContractName,
                    },
                    inputLabel: { shrink: true },
                }}
                onChange={handleRisingEventChange}
                fullWidth
                disabled={!canEditEventContractName}
                error={Boolean(errors["event_contract_name"])}
                helperText={showRisingContractMemory ? undefined : helperMessage}
                sx={{ color: theme.palette.text.primary }}
            />
        );

        const memoryBox = (
            <Grid size={{ xs: 12, sm: contractNameGridSize.memory }}>
                <Box
                    sx={{
                        p: 1.5,
                        height: "100%",
                        borderRadius: 1,
                        border: 1,
                        borderColor: (t) => alpha(t.palette.info.main, t.palette.mode === "dark" ? 0.45 : 0.35),
                        bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === "dark" ? 0.12 : 0.06),
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                        <Tooltip
                            title="Memory aid — contract name assigned on the rising event (not editable here)."
                            arrow
                        >
                            <TipsAndUpdatesOutlinedIcon
                                sx={{ fontSize: 18, color: "info.main", flexShrink: 0 }}
                                aria-label="Memory aid"
                            />
                        </Tooltip>
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", fontWeight: 600, letterSpacing: 0.2 }}
                        >
                            Rising event
                        </Typography>
                    </Stack>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                            pl: 2.75,
                            fontFamily: "Consolas, monospace",
                            fontSize: "0.8rem",
                            wordBreak: "break-word",
                            lineHeight: 1.35,
                            color: risingContractMemory ? "text.primary" : "text.disabled",
                        }}
                    >
                        {risingContractMemory || "—"}
                    </Typography>
                </Box>
            </Grid>
        );

        const contractInfoIconControl = (
            <Tooltip title={contractNameTooltip} arrow slotProps={{ tooltip: { sx: { maxWidth: 340 } } }}>
                <span style={{ display: "inline-flex" }}>
                    <InfoOutlineIcon aria-label="About event contract name" />
                </span>
            </Tooltip>
        );

        const contractInfoIcon = (
            <Grid
                size={{ xs: showRisingContractMemory ? 12 : contractNameGridSize.icon, sm: contractNameGridSize.icon }}
                sx={contractIconSx}
            >
                {contractInfoIconControl}
            </Grid>
        );

        if (showRisingContractMemory) {
            return (
                <>
                    <Grid size={12} container spacing={2} sx={{ alignItems: "center" }}>
                        <Grid size={{ xs: 12, sm: contractNameGridSize.field }}>{contractTextField}</Grid>
                        {memoryBox}
                        {contractInfoIcon}
                    </Grid>
                    {helperMessage && (
                        <Grid size={{ xs: 12, sm: contractNameGridSize.field }}>
                            <Typography
                                variant="caption"
                                color={errors["event_contract_name"] ? "error" : "text.secondary"}
                                display="block"
                                sx={{ mx: 1.75, mt: 0.5 }}
                            >
                                {helperMessage}
                            </Typography>
                        </Grid>
                    )}
                </>
            );
        }

        return (
            <>
                <Grid size={{ xs: showRenameButton ? 4 : contractNameGridSize.field }}>
                    {contractTextField}
                </Grid>
                {showRenameButton ? (
                    <Grid size={{ xs: 2 }} sx={infoIconGridSx}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Tooltip
                                title={
                                    renameDisabled
                                        ? "Save before rename"
                                        : "Rename contract for all versions (rising and resolving together)"
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        disabled={renameDisabled}
                                        onClick={onOpenRename}
                                        sx={{ whiteSpace: "nowrap" }}
                                    >
                                        Rename...
                                    </Button>
                                </span>
                            </Tooltip>
                            {contractInfoIconControl}
                        </Stack>
                    </Grid>
                ) : (
                    contractInfoIcon
                )}
            </>
        );
    };

    return (
        <Grid container spacing={2}>
            {renderDevCommentRow()}
            {formData.attributes?.manufacturer_event && <Grid size={{ xs: 12 }}>
                <InfoLine text="This is a manufacturer-provided event. It cannot be edited." />
            </Grid>}
            {renderContractNameRow()}
            {/* Event Code (nur außerhalb Configurator) */}
            {!configuratorMode && (
                <Grid size={{ xs: 2 }}>
                    <TextField
                        label="Event Code"
                        name="event_code"
                        type="number"
                        value={formData.event_code ?? 0}
                        onChange={handleRisingEventChange}
                        fullWidth
                        slotProps={{
                            input: {
                                readOnly: !canEditEventCode,
                            },
                            inputLabel: { shrink: true }
                        }}
                        disabled={(formData.version !== 0 && formData.version != null) || eventApproved}
                        error={Boolean(errors["event_code"])}
                        helperText={
                            errors["event_code"] ||
                            (!errors["event_code"] &&
                            !isAdmin &&
                            !formData.attributes?.manufacturer_event
                                ? "Only administrators can change the event code."
                                : undefined)
                        }
                        sx={{ color: theme.palette.text.primary }}
                    />
                </Grid>
            )}
            {!configuratorMode && <Grid size={{ xs: 1 }} sx={infoIconGridSx}>
                <Tooltip
                    title={
                        "Unique identifier of the event (current system code). Changing this for events that are " +
                        "already approved should only be done in coordination with an administrator."
                    }
                    arrow
                >
                    <span style={{ display: "inline-flex" }}>
                        <InfoOutlineIcon />
                    </span>
                </Tooltip>
            </Grid>}
            {!configuratorMode && <Grid size={{ xs: 2 }}>
                <TextField
                    label="Legacy code"
                    name="event_code_old"
                    type="number"
                    value={formData.attributes?.event_code_old ?? 0}
                    onChange={handleAttributeChange}
                    fullWidth
                    slotProps={{
                        input: {
                            readOnly: isReader || !canEditEventCodeOld,
                        },
                        inputLabel: {
                            shrink: true
                        }
                    }}
                />
            </Grid>}
            {!configuratorMode && <Grid size={{ xs: 1 }} sx={infoIconGridSx}>
                <Tooltip title="Legacy event identifier (previous event system)" arrow>
                    <span style={{ display: "inline-flex" }}>
                        <InfoOutlineIcon />
                    </span>
                </Tooltip>
            </Grid>}
            {!configuratorMode && (
                <Grid size={{ xs: 2 }}>
                    <FormControl fullWidth error={Boolean(errors["event_type"])}>
                        <InputLabel id="event_type-label">Event Type</InputLabel>
                        <Select
                            labelId="event_type-label"
                            name="event_type"
                            value={formData.event_type ?? ""}
                            onChange={handleSelectChange}
                            label="Event Type"
                            slotProps={{
                                input: { readOnly: isReader || formData.attributes?.manufacturer_event },
                            }}
                            disabled={eventApproved}
                            required
                            sx={{ color: theme.palette.text.primary }}
                        >
                            <MenuItem value=""><em>Select...</em></MenuItem>
                            <MenuItem value="UserEvent">UserEvent</MenuItem>
                            <MenuItem value="SystemEvent">SystemEvent</MenuItem>
                            <MenuItem value="SystemError">SystemError</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            )}

            {!configuratorMode && <Grid size={{ xs: 1 }} sx={infoIconGridSx}>
                <Tooltip title="Classification of the event" arrow>
                    <span style={{ display: "inline-flex" }}>
                        <InfoOutlineIcon />
                    </span>
                </Tooltip>
            </Grid>}
            {!(configuratorMode && creatingNewResolvingEvent) && (
            <Grid size={{ xs: 2 }}>
                <FormControl fullWidth error={Boolean(errors["attributes.severity"])}>
                    <InputLabel id="severity-label">Severity</InputLabel>
                    <Select
                        labelId="severity-label"
                        name="severity"
                        value={formData.attributes?.severity ?? ""}
                        onChange={handleAttributeSelectChange}
                        label="Severity"
                        slotProps={{
                            input: {
                                readOnly: isReader || formData.attributes?.manufacturer_event
                            },
                        }}
                        required
                        disabled={creatingNewResolvingEvent && !configuratorMode}
                        sx={{ color: theme.palette.text.primary }}
                    >
                        <MenuItem value=""><em>Select...</em></MenuItem>
                        <MenuItem value="Info">Info</MenuItem>
                        <MenuItem value="Warning">Warning</MenuItem>
                        <MenuItem value="Error">Error</MenuItem>
                        <MenuItem value="Fatal">Fatal</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            )}

            {!(configuratorMode && creatingNewResolvingEvent) && (
            <Grid size={{ xs: 1 }} sx={infoIconGridSx}>
                <Tooltip title="Indicates the criticality level of the event" arrow>
                    <span style={{ display: "inline-flex" }}>
                        <InfoOutlineIcon />
                    </span>
                </Tooltip>
            </Grid>
            )}
            {!configuratorMode && showSourceField && <Grid size={{ xs: 5 }}>
                <TextField
                    label="Source"
                    name="source"
                    value={formData.attributes?.source ?? ""}
                    slotProps={{
                        input: {
                            readOnly: isReader || formData.attributes?.manufacturer_event
                        },
                        inputLabel: {
                            shrink: true
                        }
                    }}
                    onChange={handleAttributeChange}
                    fullWidth
                />
            </Grid>}
            {!configuratorMode && showSourceField &&
                <Grid size={{ xs: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', color: theme.palette.primary.main }}>
                    <Tooltip title="Optional" arrow>
                        <InfoOutlineIcon />
                    </Tooltip>
                </Grid>}
            {!configuratorMode && showAlarmAndMessageHistoryFields && (
                <>
                    <Grid size={{ xs: 3 }} sx={{ display: 'flex', alignItems: 'center', }}>
                        <FormControlLabel
                            sx={{ mr: 1 }}
                            control={
                                <Checkbox
                                    checked={Boolean(formData.attributes?.alarm)}
                                    onChange={handleCheckboxChange}
                                    name="alarm"
                                />
                            }
                            label="Alarm"
                            style={{ color: theme.palette.text.primary }}
                            disabled={isReader || formData.attributes?.manufacturer_event}
                        />
                        <Tooltip title="Specifies whether the event triggers an alarm" arrow sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', color: theme.palette.primary.main }}>
                            <InfoOutlineIcon />
                        </Tooltip>
                    </Grid>
                    <Grid size={{ xs: 5 }} sx={{ display: 'flex', alignItems: 'center', }}>
                        <FormControlLabel
                            sx={{ mr: 1 }}
                            control={
                                <Checkbox
                                    checked={Boolean(formData.attributes?.message_history)}
                                    onChange={handleCheckboxChange}
                                    name="message_history"
                                />
                            }
                            label="Message History"
                            style={{ color: theme.palette.text.primary }}
                            disabled={isReader || formData.attributes?.manufacturer_event}
                        />
                        <Tooltip title="Event will be shown in message history" arrow sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', color: theme.palette.primary.main }}>
                            <InfoOutlineIcon />
                        </Tooltip>
                    </Grid>
                </>
            )}
        </Grid>
    );
});
